// OpenAI ChatGPT Ads Pixel (`oaiq`) + Conversions API dual-tracking.
//
// Fetches the Pixel ID from the backend on first use, injects the
// bzrcdn.openai.com loader script exactly once, and provides
// `trackOpenai(event, custom, user)` which fires BOTH the browser pixel
// AND a server-side CAPI mirror. Events share an `event_id` so OpenAI
// deduplicates by (pixel_id + event_name + event_id).
//
// Zero-config: if no Pixel ID is set in the admin, this is a silent no-op.
// Mirrors the shape of `metaPixel.js` so both providers can run in
// parallel without code churn at call sites.

import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { hasMarketingConsent, onConsentChange } from "@/lib/consent";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = BASE ? `${BASE.replace(/\/$/, "")}/api` : "/api";

// Standard `oaiq` events per OpenAI docs. Anything else is fired via
// `oaiq("measure", "<custom>", ...)` too — OpenAI's schema is lenient
// about custom event names.
const STANDARD_EVENTS = new Set([
  "PageView",
  "ViewContent",
  "AddToCart",
  "InitiateCheckout",
  "Purchase",
  "Search",
  "AddToWishlist",
  "CompleteRegistration",
  "Lead",
]);

let _pixelId = null;
let _initPromise = null;
let _initialised = false;

function injectPixel(id) {
  if (typeof window === "undefined") return;
  if (window.oaiq) {
    if (!_initialised) {
      window.oaiq("init", { pixelId: id });
      _initialised = true;
    }
    return;
  }
  /* eslint-disable */
  // Loader per https://developers.openai.com/ads/measurement-pixel — the
  // queue pattern mirrors Meta's fbq loader so retries during page-load
  // don't drop early events.
  !(function (w, d, s, u) {
    if (w.oaiq) return;
    const n = (w.oaiq = function () {
      n.callMethod
        ? n.callMethod.apply(n, arguments)
        : n.queue.push(arguments);
    });
    n.queue = [];
    n.loaded = true;
    n.version = "1.0";
    const t = d.createElement(s);
    t.async = true;
    t.src = u;
    const g = d.getElementsByTagName(s)[0];
    g.parentNode.insertBefore(t, g);
  })(window, document, "script", "https://bzrcdn.openai.com/oaiq.js");
  /* eslint-enable */
  window.oaiq("init", { pixelId: id });
  _initialised = true;
}

async function ensureInitialised() {
  if (!hasMarketingConsent()) return null; // Dormant until user opts in
  if (_initialised) return _pixelId;
  if (_initPromise) return _initPromise;
  _initPromise = axios
    .get(`${API}/tracking/openai/config`)
    .then((r) => {
      const id = r?.data?.pixel_id;
      if (id) {
        _pixelId = id;
        injectPixel(id);
      }
      return _pixelId;
    })
    .catch(() => null);
  return _initPromise;
}

/** Fire an OpenAI Ads event on both the browser `oaiq` pixel AND the
 *  server CAPI. Returns silently if no Pixel is configured. */
export async function trackOpenai(eventName, customData = {}, userData = {}) {
  try {
    if (!hasMarketingConsent()) return; // Respect visitor's opt-out choice
    const id = await ensureInitialised();
    if (!id) return;
    const eventId = uuidv4();

    // 1. Browser pixel — `oaiq("measure", name, custom, { event_id })`
    if (typeof window !== "undefined" && window.oaiq) {
      window.oaiq("measure", eventName, customData, { event_id: eventId });
    }

    // 2. Server-side CAPI mirror (fire-and-forget). Same event_id so
    //    OpenAI dedupes browser + server hits into a single conversion.
    axios
      .post(`${API}/tracking/openai/events`, {
        event_name: eventName,
        event_id: eventId,
        custom_data: customData,
        user_data: userData,
        event_source_url:
          typeof window !== "undefined" ? window.location.href : undefined,
      })
      .catch(() => {
        /* silent — never surface pixel errors */
      });
  } catch {
    /* never break the app on a tracking error */
  }

  // Mark param used to keep bundlers from tree-shaking the constant.
  void STANDARD_EVENTS;
}

/** Convenience wrappers — same payload shape as `metaPixel.js` so a
 *  single call at each site fires both providers cleanly. */
export const openaiPageView = () => trackOpenai("PageView");

export const openaiViewContent = (product, variation) =>
  trackOpenai("ViewContent", {
    content_ids: [String(product.id || product.slug)],
    content_type: "product",
    content_name: product.name,
    content_category:
      product.categories?.[0]?.name ||
      product.categories?.[0]?.slug ||
      undefined,
    value: Number(variation?.price || product.sale_price || product.price || 0),
    currency: "INR",
  });

export const openaiAddToCart = (item) =>
  trackOpenai("AddToCart", {
    content_ids: [String(item.id || item.slug)],
    content_type: "product",
    content_name: item.name,
    value: Number(item.price || 0) * Number(item.quantity || 1),
    currency: "INR",
    contents: [
      {
        id: String(item.id || item.slug),
        quantity: Number(item.quantity || 1),
        item_price: Number(item.price || 0),
      },
    ],
  });

export const openaiInitiateCheckout = (items, total, form = {}) =>
  trackOpenai(
    "InitiateCheckout",
    {
      content_ids: (items || []).map((i) => String(i.id || i.slug)),
      content_type: "product",
      num_items: (items || []).reduce((s, i) => s + Number(i.quantity || 1), 0),
      value: Number(total || 0),
      currency: "INR",
      contents: (items || []).map((i) => ({
        id: String(i.id || i.slug),
        quantity: Number(i.quantity || 1),
        item_price: Number(i.price || 0),
      })),
    },
    {
      email: form.email || undefined,
      phone: form.phone || undefined,
      first_name: form.name?.split(" ")?.[0] || undefined,
      last_name: form.name?.split(" ")?.slice(1)?.join(" ") || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      zip: form.pincode || undefined,
      country: form.country || "IN",
    },
  );

export const openaiPurchase = (order, items) =>
  trackOpenai(
    "Purchase",
    {
      content_ids: (items || []).map((i) => String(i.id || i.slug)),
      content_type: "product",
      num_items: (items || []).reduce((s, i) => s + Number(i.quantity || 1), 0),
      value: Number(order?.total || 0),
      currency: "INR",
      order_id: order?.order_number,
      contents: (items || []).map((i) => ({
        id: String(i.id || i.slug),
        quantity: Number(i.quantity || 1),
        item_price: Number(i.price || 0),
      })),
    },
    {
      email: order?.shipping?.email || undefined,
      phone: order?.shipping?.phone || undefined,
      first_name: order?.shipping?.name?.split(" ")?.[0] || undefined,
      last_name: order?.shipping?.name?.split(" ")?.slice(1)?.join(" ") || undefined,
      city: order?.shipping?.city || undefined,
      state: order?.shipping?.state || undefined,
      zip: order?.shipping?.pincode || undefined,
      country: order?.shipping?.country || "IN",
      external_id: order?.order_number,
    },
  );

export const openaiSearch = (query) =>
  trackOpenai("Search", { search_string: query });

export const openaiAddToWishlist = (product) =>
  trackOpenai("AddToWishlist", {
    content_ids: [String(product.id || product.slug)],
    content_type: "product",
    content_name: product.name,
    value: Number(product.sale_price || product.price || 0),
    currency: "INR",
  });

// Initialise as soon as the module is imported — gated on marketing
// consent. If the visitor accepts later, re-initialise and fire a
// belated PageView so ads-manager funnel counts stay accurate.
if (typeof window !== "undefined") {
  ensureInitialised();
  onConsentChange((v) => {
    if (v === "accepted") {
      ensureInitialised().then((id) => {
        if (id && window.oaiq) window.oaiq("measure", "PageView");
      });
    }
  });
}
